import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type AdyenPaymentModuleSpecsFragment } from '../../../../api/fragments/AdyenPaymentModuleSpecs';
import {
    DeleteAdyenPaymentSettingsDocument,
    type DeleteAdyenPaymentSettingsMutation,
    type DeleteAdyenPaymentSettingsMutationVariables,
} from '../../../../api/mutations/deleteAdyenPaymentSettings';

export type AdyenPaymentSettingActionsProps = {
    module: AdyenPaymentModuleSpecsFragment;
    paymentSetting: AdyenPaymentModuleSpecsFragment['paymentSettings'][number];
};

const AdyenPaymentSettingActions = ({ module, paymentSetting }: AdyenPaymentSettingActionsProps) => {
    const moduleId = module.id;
    const paymentSettingId = paymentSetting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['adyenPaymentModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('adyenPaymentModuleDetails:paymentSettings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('adyenPaymentModuleDetails:paymentSettings.deleteModal.content'),
            okText: t('adyenPaymentModuleDetails:paymentSettings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('adyenPaymentModuleDetails:paymentSettings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('adyenPaymentModuleDetails:paymentSettings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteAdyenPaymentSettingsMutation, DeleteAdyenPaymentSettingsMutationVariables>({
                        mutation: DeleteAdyenPaymentSettingsDocument,
                        variables: { moduleId, id: paymentSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('adyenPaymentModuleDetails:paymentSettings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, paymentSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/adyen/${paymentSettingId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('adyenPaymentModuleDetails:paymentSettingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('adyenPaymentModuleDetails:paymentSettingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default AdyenPaymentSettingActions;
